import { T, useGT } from 'gt-react';
import type { CredentialSummaryResponse } from '@erp/rpc';
import { Label } from 'src/components/ui/label';
import { Autocomplete } from 'src/components/common/inputs/autocomplete';
import { pickerLabels } from 'src/components/common/inputs/pickerLabels';
import type { RemoteDesktopApplicationCredentialsConfig } from './remote-desktop-config';

export type RemoteDesktopAppCredentialsFieldProps = {
  applicationCredentials: RemoteDesktopApplicationCredentialsConfig;
  onApplicationCredentialsChange: (
    updater: (
      current: RemoteDesktopApplicationCredentialsConfig,
    ) => RemoteDesktopApplicationCredentialsConfig,
  ) => void;
  passwordCredentials: CredentialSummaryResponse[];
};

/**
 * Shared multi-select picker for in-desktop application credentials. Consumed
 * by the `remote_desktop_execute` operator to log into apps running inside the
 * remote desktop. Rendered on the agent tools tab (login config) and the
 * `remote_desktop_execute` workflow step renderer so both stay in sync.
 */
export function RemoteDesktopAppCredentialsField({
  applicationCredentials,
  onApplicationCredentialsChange,
  passwordCredentials,
}: RemoteDesktopAppCredentialsFieldProps) {
  const gt = useGT();

  return (
    <div className="space-y-2">
      <Label>
        <T context="label for optional app credentials multiselect">
          Desktop Application Credentials (optional)
        </T>
      </Label>
      <Autocomplete
        multiple
        items={passwordCredentials}
        value={applicationCredentials.credentialIds}
        onValueChange={(values) =>
          onApplicationCredentialsChange((current) => ({
            ...current,
            credentialIds: values,
          }))
        }
        getValue={(cred) => cred.id}
        getLabel={(cred) =>
          cred.usernameMasked
            ? `${cred.name} (${cred.usernameMasked})`
            : cred.name
        }
        labels={pickerLabels({
          placeholder: gt('Select credentials...', {
            context:
              'placeholder for selecting app credentials in remote desktop',
          }),
          search: gt('Search credentials...', {
            context: 'search placeholder for credential picker',
          }),
          empty: gt('No password credentials found.', {
            context: 'empty state credential search',
          }),
        })}
      />
      <p className="text-xs text-muted-foreground">
        <T context="help text for desktop application credentials">
          Select one or more saved credentials for applications inside the
          remote desktop. Manage credentials in Org Settings.
        </T>
      </p>
    </div>
  );
}
